import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../services/api";
const BATCH_SIZE = 5;
/**
 * Loads result-card detail a batch at a time, staying one batch ahead of what has been revealed.
 *
 * Each profile costs an Airscale credit, so nothing is fetched for results the user never
 * scrolls to. Revealing a batch requests the one after it, and the first reveal requests two, so
 * scrolling lands on detail that is already there.
 *
 * Batches are requested separately rather than as one call, so the first five cards appear
 * without waiting for the five being prefetched behind them.
 *
 * Everything is tracked by linkedInId rather than by position, because the result list grows
 * while the extension is still scrolling and the final scrape can insert rows that the partial
 * passes withheld. Positions shift; ids do not.
 *
 * `retryKey` changes when the scrape finishes. A row streamed mid-scroll can be asked for before
 * the API has stored it, which answers with no detail; without the retry those first cards —
 * exactly the ones on screen — would keep their scraped fields forever.
 */
export const useProfileEnrichment = (sessionId, token, linkedInIds, resetKey, retryKey) => {
    const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
    const [profiles, setProfiles] = useState({});
    const [settledIds, setSettledIds] = useState(new Set());
    const requestedIds = useRef(new Set());
    // A new page or a new search starts over. Adjusting during render rather than in an effect
    // means no batch is ever requested against the previous page's rows.
    const [loadedKey, setLoadedKey] = useState(resetKey);
    if (loadedKey !== resetKey) {
        setLoadedKey(resetKey);
        requestedIds.current = new Set();
        setSettledIds(new Set());
        setProfiles({});
        setVisibleCount(BATCH_SIZE);
    }
    // Asking again is cheap: a profile already fetched is served from the Airscale cache, and one
    // the API has no detail for simply answers empty a second time. The effect below picks this up
    // without needing retryKey in its deps, because the same update that ends the scrape also
    // replaces the results array, and so linkedInIds.
    const [retriedKey, setRetriedKey] = useState(retryKey);
    if (retriedKey !== retryKey) {
        setRetriedKey(retryKey);
        for (const id of linkedInIds) {
            if (!profiles[id])
                requestedIds.current.delete(id);
        }
    }
    useEffect(() => {
        if (!(sessionId && token))
            return;
        const wanted = linkedInIds.slice(0, visibleCount + BATCH_SIZE).filter((id) => !requestedIds.current.has(id));
        if (wanted.length === 0)
            return;
        for (const id of wanted)
            requestedIds.current.add(id);
        for (let offset = 0; offset < wanted.length; offset += BATCH_SIZE) {
            const batch = wanted.slice(offset, offset + BATCH_SIZE);
            api
                .enrichSessionProfiles(token, sessionId, batch)
                .then((response) => {
                setProfiles((prev) => {
                    const next = Object.assign({}, prev);
                    for (const profile of response.data.profiles)
                        next[profile.linkedInId] = profile;
                    return next;
                });
            })
                // A card whose detail never arrives still renders, with its scraped fields only.
                .catch(() => undefined)
                .finally(() => setSettledIds((prev) => {
                const next = new Set(prev);
                for (const id of batch)
                    next.add(id);
                return next;
            }));
        }
    }, [sessionId, token, linkedInIds, visibleCount]);
    // Cards appear in order and only once their own lookup has finished, success or failure, so a
    // slow profile holds up the ones behind it rather than leaving a gap.
    const readyCount = useMemo(() => {
        let ready = 0;
        while (ready < linkedInIds.length && settledIds.has(linkedInIds[ready]))
            ready++;
        return ready;
    }, [settledIds, linkedInIds]);
    const shownCount = Math.min(visibleCount, readyCount);
    const revealMore = useCallback(() => {
        setVisibleCount((current) => Math.min(current + BATCH_SIZE, linkedInIds.length));
    }, [linkedInIds.length]);
    return {
        profiles,
        shownCount,
        hasMore: shownCount < linkedInIds.length,
        isRevealing: shownCount < Math.min(visibleCount, linkedInIds.length),
        revealMore,
    };
};
